import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { fmtDist, fmtDur, fmtDate } from "@/lib/api";
import { RouteOverviewMap } from "@/components/RouteOverviewMap";
import { Printer, ArrowLeft } from "lucide-react";

export default function RouteSheetPage() {
  const { id } = useParams();
  const [route, setRoute] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/routes/${id}`).then((r) => setRoute(r.data)).catch(() => setError(true));
  }, [id]);

  if (error) return <div className="min-h-screen bg-background text-muted-foreground flex items-center justify-center">Route not found.</div>;
  if (!route) return <div className="min-h-screen bg-background text-muted-foreground flex items-center justify-center">Loading…</div>;

  return (
    <div className="min-h-screen bg-background print:bg-white py-8 print:py-0">
      <div className="max-w-[210mm] mx-auto mb-4 flex justify-between items-center px-2 print:hidden">
        <Link to="/routes" data-testid="sheet-back-link" className="text-muted-foreground text-xs uppercase tracking-widest inline-flex gap-2 items-center hover:text-foreground">
          <ArrowLeft size={14} /> Route planner
        </Link>
        <button
          data-testid="print-sheet-button"
          onClick={() => window.print()}
          className="bg-[#FF3B30] text-white px-5 py-2.5 text-xs font-bold uppercase tracking-widest hover:bg-[#ff554b] inline-flex items-center gap-2"
        >
          <Printer size={14} /> Print sheet
        </button>
      </div>

      <div className="max-w-[210mm] mx-auto bg-white text-black p-10 print:p-4" data-testid="route-sheet">
        <div className="flex justify-between items-start border-b-4 border-black pb-4">
          <div>
            <div className="font-heading font-black text-3xl tracking-tighter leading-none">
              LEO'S<span className="text-[#FF3B30]">FLEET</span>
            </div>
            <div className="uppercase text-[10px] tracking-[0.3em] font-bold text-neutral-500 mt-1">Driver Route Sheet</div>
          </div>
          <div className="text-right">
            <div className="inline-block bg-[#FFD60A] border-2 border-black font-mono font-black text-xl px-3 py-1 tracking-wider">{route.vehicle_reg}</div>
            <div className="text-sm mt-1 font-semibold">{fmtDate(route.date)}</div>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-4 py-4 border-b border-neutral-300 text-sm">
          <div>
            <div className="uppercase text-[9px] tracking-[0.2em] font-bold text-neutral-500">Driver</div>
            <div className="font-bold">{route.driver_name || "________________"}</div>
          </div>
          <div>
            <div className="uppercase text-[9px] tracking-[0.2em] font-bold text-neutral-500">Start / return</div>
            <div className="font-bold">{route.yard?.name || "Yard"} · {route.yard?.postcode}</div>
          </div>
          <div>
            <div className="uppercase text-[9px] tracking-[0.2em] font-bold text-neutral-500">Total distance</div>
            <div className="font-bold">{fmtDist(route.total_distance_m)}</div>
          </div>
          <div>
            <div className="uppercase text-[9px] tracking-[0.2em] font-bold text-neutral-500">Est. drive time</div>
            <div className="font-bold">{fmtDur(route.total_duration_s)}</div>
          </div>
        </div>

        <table className="w-full text-sm mt-4">
          <thead>
            <tr className="border-b-2 border-black text-left">
              <th className="py-2 pr-2 uppercase text-[9px] tracking-[0.2em] font-bold w-8">#</th>
              <th className="py-2 pr-2 uppercase text-[9px] tracking-[0.2em] font-bold">Store</th>
              <th className="py-2 pr-2 uppercase text-[9px] tracking-[0.2em] font-bold">Postcode</th>
              <th className="py-2 pr-2 uppercase text-[9px] tracking-[0.2em] font-bold">Leg</th>
              <th className="py-2 pr-2 uppercase text-[9px] tracking-[0.2em] font-bold">Drive</th>
              <th className="py-2 pr-2 uppercase text-[9px] tracking-[0.2em] font-bold w-20">Arrived</th>
              <th className="py-2 uppercase text-[9px] tracking-[0.2em] font-bold w-28">Signature</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-neutral-300 bg-neutral-100">
              <td className="py-2 pr-2 font-mono font-black">Y</td>
              <td className="py-2 pr-2 font-bold" colSpan={2}>DEPART {route.yard?.name?.toUpperCase() || "YARD"} ({route.yard?.postcode})</td>
              <td className="py-2 pr-2" colSpan={2}></td>
              <td className="py-2 pr-2 text-neutral-400">____:____</td>
              <td className="py-2"></td>
            </tr>
            {route.stops.map((s) => (
              <tr key={s.store_id} className="border-b border-neutral-300">
                <td className="py-2.5 pr-2 font-mono font-black">{s.order}</td>
                <td className="py-2.5 pr-2 font-semibold">{s.name}</td>
                <td className="py-2.5 pr-2 font-mono">{s.postcode}</td>
                <td className="py-2.5 pr-2">{fmtDist(s.leg_distance_m)}</td>
                <td className="py-2.5 pr-2">{fmtDur(s.leg_duration_s)}</td>
                <td className="py-2.5 pr-2 text-neutral-400">____:____</td>
                <td className="py-2.5 border-b border-neutral-400"></td>
              </tr>
            ))}
            <tr className="border-b-2 border-black bg-neutral-100">
              <td className="py-2 pr-2 font-mono font-black">Y</td>
              <td className="py-2 pr-2 font-bold" colSpan={2}>RETURN TO {route.yard?.name?.toUpperCase() || "YARD"}</td>
              <td className="py-2 pr-2">{fmtDist(route.return_leg?.distance_m || 0)}</td>
              <td className="py-2 pr-2">{fmtDur(route.return_leg?.duration_s || 0)}</td>
              <td className="py-2 pr-2 text-neutral-400">____:____</td>
              <td className="py-2"></td>
            </tr>
          </tbody>
        </table>

        <div className="mt-6 border border-neutral-300 p-3">
          <div className="uppercase text-[9px] tracking-[0.2em] font-bold text-neutral-500 mb-1">Notes / issues</div>
          <div className="h-14"></div>
        </div>

        {route.geometry?.length > 1 && (
          <div className="mt-6 route-sheet-map" data-testid="route-sheet-map" style={{ breakInside: "avoid" }}>
            <div className="uppercase text-[9px] tracking-[0.2em] font-bold text-neutral-500 mb-2">Route overview map</div>
            <div className="border-2 border-black overflow-hidden">
              <RouteOverviewMap route={route} height={320} />
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-8 mt-8 text-sm">
          {["Driver signature", "Time out", "Time back"].map((l) => (
            <div key={l}>
              <div className="border-b-2 border-black h-8"></div>
              <div className="uppercase text-[9px] tracking-[0.2em] font-bold text-neutral-500 mt-1">{l}</div>
            </div>
          ))}
        </div>

        <div className="mt-6 text-[9px] text-neutral-400 uppercase tracking-widest">
          Generated {new Date().toLocaleDateString("en-GB")} · {route.stops.length} stops · Leo's Fleet Transport Ops
        </div>
      </div>
    </div>
  );
}
